// Career mode configuration: board season objectives + rival club offers.
// The board reacts to how the club's league season ends; offers from other
// clubs appear as the manager's reputation grows.

import type { Rng } from '@/game/utils/rng'
import { defaultRng, rollInt } from '@/game/utils/rng'
import type { Difficulty } from './difficultyConfig'
import type { LeagueSeasonEndedPayload } from '@/game/utils/EventBus'
import { DIVISIONS, HIGHEST_DIVISION, LOWEST_DIVISION, SEASON } from './leagueConfig'

export type ObjectiveType = 'avoid_relegation' | 'top_half' | 'promotion' | 'title'

export interface SeasonObjective {
  type: ObjectiveType
  label: string
  /** Confidence delta applied when the objective is met */
  confidenceOnSuccess: number
  /** Confidence delta (negative) when failed, before difficulty leniency */
  confidenceOnFailure: number
}

const OBJECTIVE_DEFS: Record<ObjectiveType, Omit<SeasonObjective, 'type'>> = {
  avoid_relegation: { label: 'Evitar el descenso', confidenceOnSuccess: 8, confidenceOnFailure: -26 },
  top_half: { label: 'Terminar en la mitad alta', confidenceOnSuccess: 12, confidenceOnFailure: -18 },
  promotion: { label: 'Lograr el ascenso', confidenceOnSuccess: 20, confidenceOnFailure: -16 },
  title: { label: 'Ganar el título', confidenceOnSuccess: 26, confidenceOnFailure: -20 },
}

function objective(type: ObjectiveType): SeasonObjective {
  return { type, ...OBJECTIVE_DEFS[type] }
}

export const BOARD_CONFIG = {
  startingConfidence: 60,
  maxConfidence: 100,
  fireThreshold: 0,
  /** Manager reputation deltas, kept smaller than confidence swings */
  reputationOnSuccess: 10,
  reputationOnFailure: -5,
} as const

/** The board sets the season's objective from division, last finish and difficulty */
export function pickSeasonObjective(
  division: number,
  lastPosition: number | null,
  difficulty: Difficulty,
): SeasonObjective {
  const lenient = difficulty === 'easy'
  const demanding = difficulty === 'hard'

  if (division === HIGHEST_DIVISION) {
    if (demanding) return objective('title')
    if (lenient) return objective('avoid_relegation')
    return objective('top_half')
  }

  // Lower divisions: the board wants the club climbing
  if (demanding) return objective('promotion')
  // Lenient: survival — but the lowest division has no relegation, so ask for
  // a respectable mid-table finish instead of a free pass.
  if (lenient) return objective(division === LOWEST_DIVISION ? 'top_half' : 'avoid_relegation')
  // Normal: demand promotion after a strong finish, otherwise a solid mid-table
  if (lastPosition !== null && lastPosition <= SEASON.clubsPerDivision / 2) return objective('promotion')
  return objective('top_half')
}

/** Did the club meet the objective, given how the season ended? */
export function evaluateObjective(obj: SeasonObjective, payload: LeagueSeasonEndedPayload): boolean {
  switch (obj.type) {
    case 'avoid_relegation':
      return !payload.relegated
    case 'top_half':
      return payload.position <= SEASON.clubsPerDivision / 2
    case 'promotion':
      return payload.promoted || payload.title
    case 'title':
      return payload.title
  }
}

// --- Club offers (changing clubs) ------------------------------------------

export interface ClubOffer {
  id: string
  clubName: string
  division: number // league division the new club competes in
  startingClubLevel: number // 0-7
  startingMoney: number
  prestige: number // manager reputation roughly required to attract it
  description: string
}

const OFFER_CLUB_NAMES = [
  'Padel Capital', 'CD Olímpico', 'Club Vista Mar', 'Padel Nacional',
  'CP Gran Arena', 'Club Élite 360', 'Padel Continental', 'CD Maestros',
  'Club Horizonte', 'Padel Imperial', 'CP Cumbre', 'Club Atlántico',
]

/**
 * Offers scale with manager reputation: a renowned manager attracts
 * higher-division, better-funded clubs. Deterministic with a seeded rng.
 */
export function generateClubOffers(managerReputation: number, rng: Rng = defaultRng): ClubOffer[] {
  // Below a minimum reputation, nobody comes calling
  if (managerReputation < 15) return []

  const count = managerReputation >= 45 ? 2 : 1
  const used = new Set<string>()
  const offers: ClubOffer[] = []

  for (let i = 0; i < count; i++) {
    // Higher reputation unlocks higher divisions (lower number) and bigger clubs
    const tierRoll = Math.min(100, managerReputation + rollInt(-10, 15, rng))
    const division = tierRoll >= 80 ? HIGHEST_DIVISION
      : tierRoll >= 60 ? 2
      : tierRoll >= 40 ? 3
      : LOWEST_DIVISION
    const startingClubLevel = Math.max(2, Math.min(6, Math.round(tierRoll / 18)))

    let name = OFFER_CLUB_NAMES[rollInt(0, OFFER_CLUB_NAMES.length - 1, rng)]
    let guard = 0
    while (used.has(name) && guard++ < 10) {
      name = OFFER_CLUB_NAMES[rollInt(0, OFFER_CLUB_NAMES.length - 1, rng)]
    }
    used.add(name)

    const startingMoney = 8000 + (7 - division) * 6000 + startingClubLevel * 2000

    offers.push({
      id: `offer-${division}-${i}-${rollInt(1000, 9999, rng)}`,
      clubName: name,
      division,
      startingClubLevel,
      startingMoney,
      prestige: tierRoll,
      description: `${DIVISIONS[division].name} · empezarías en nivel ${startingClubLevel}`,
    })
  }

  return offers
}
